import { useState, useEffect, useCallback } from 'react';
import { getUserTeams } from 'team/apis/teamApi';
import { getSubteam } from 'team/apis/teamSubteamApi';
import { useAuth } from 'contexts/AuthContext';

/**
 * Custom hook to load and check settings restrictions
 * @returns {Object} { restrictions, isRestricted, isTeamAdmin, isSubteamAdmin, loading }
 */
export const useSettingsRestrictions = () => {
    const { authState } = useAuth();
    const userId = authState?.user?.userId;

    const [settingsRestrictions, setSettingsRestrictions] = useState({
        lockAll: false,
        fields: {
            'Exporting Leads to CRM': false,
            'Downloading leads (CSV)': false,
            'Downloading leads as contacts (.vcf)': false,
            'Exporting Insights Data (CSV)': false,
            'Email Signature': false,
            'Virtual Backgrounds': false,
            'Branded virtual backgrounds': false,
            'Create new digital cards': false,
            'Switch Cards': false,
            'Account Email': false,
            'Follow up email': false,
            'Touch branding': false,
            'Change Touch URL': false
        }
    });
    const [loadingRestrictions, setLoadingRestrictions] = useState(false);
    const [isTeamAdmin, setIsTeamAdmin] = useState(false);
    const [isSubteamAdmin, setIsSubteamAdmin] = useState(false);

    // Load Settings restrictions
    const loadSettingsRestrictions = useCallback(async () => {
        if (!userId) return;

        setLoadingRestrictions(true);
        try {
            const teamsResponse = await getUserTeams();
            const teams = teamsResponse?.teams || teamsResponse || [];

            const subteamIds = [];
            let userIsTeamAdmin = false;
            let userIsSubteamAdmin = false;

            teams.forEach(team => {
                const membership = team.membershipInfo || team;
                if (membership.role === 'admin') {
                    userIsTeamAdmin = true;
                }
                if (membership.isSubteamAdmin) {
                    userIsSubteamAdmin = true;
                }
                if (membership.subteam?._id) {
                    subteamIds.push(membership.subteam._id);
                }
            });

            setIsTeamAdmin(userIsTeamAdmin);
            setIsSubteamAdmin(userIsSubteamAdmin);

            // If user is admin, no restrictions apply
            if (userIsTeamAdmin || userIsSubteamAdmin) {
                setLoadingRestrictions(false);
                return;
            }

            const mergedRestrictions = {
                lockAll: false,
                fields: {
                    'Exporting Leads to CRM': false,
                    'Downloading leads (CSV)': false,
                    'Downloading leads as contacts (.vcf)': false,
                    'Exporting Insights Data (CSV)': false,
                    'Email Signature': false,
                    'Virtual Backgrounds': false,
                    'Branded virtual backgrounds': false,
                    'Create new digital cards': false,
                    'Switch Cards': false,
                    'Account Email': false,
                    'Follow up email': false,
                    'Touch branding': false,
                    'Change Touch URL': false
                }
            };

            // Load restrictions from all subteams (if any subteam restricts, it's restricted)
            for (const subteamId of subteamIds) {
                try {
                    const subteamResponse = await getSubteam(subteamId);
                    const subteam = subteamResponse?.subteam || subteamResponse;
                    const subteamRestrictions = subteam?.settings?.restrictions || {};

                    if (subteamRestrictions.settings) {
                        const settingsRestrictions = subteamRestrictions.settings;
                        // If any subteam has lockAll, it's restricted
                        if (settingsRestrictions.lockAll) {
                            mergedRestrictions.lockAll = true;
                        }
                        // Merge field restrictions (if any subteam restricts a field, it's restricted)
                        if (settingsRestrictions.fields) {
                            Object.keys(mergedRestrictions.fields).forEach(field => {
                                if (settingsRestrictions.fields[field] === true) {
                                    mergedRestrictions.fields[field] = true;
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.error(`Error loading restrictions from subteam ${subteamId}:`, error);
                }
            }

            setSettingsRestrictions(mergedRestrictions);
        } catch (error) {
            console.error('Error loading settings restrictions:', error);
        } finally {
            setLoadingRestrictions(false);
        }
    }, [userId]);

    useEffect(() => {
        loadSettingsRestrictions();
    }, [loadSettingsRestrictions]);

    // Helper function to check if a settings feature is restricted
    const isRestricted = useCallback((featureName) => {
        if (isTeamAdmin || isSubteamAdmin) return false; // Admins are not restricted
        if (settingsRestrictions.lockAll) return true; // If lockAll is enabled, all features are restricted
        return settingsRestrictions.fields[featureName] === true;
    }, [settingsRestrictions, isTeamAdmin, isSubteamAdmin]);

    return {
        restrictions: settingsRestrictions,
        isRestricted,
        isTeamAdmin,
        isSubteamAdmin,
        loading: loadingRestrictions
    };
};

export default useSettingsRestrictions;

